import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';
import { compressImage } from '../utils/compressImage';
import '../styles/ProfilePage.css';


const TARGET_OPTIONS = [
  { id: 'local', label: '🏠 Yerli müştəri', desc: 'Şəhər sakinləri üçün' },
  { id: 'corporate', label: '🏢 Korporativ', desc: 'Şirkət tədbirləri' },
];

function parseGoogleMapsCoords(url) {
  if (!url) return null;
  // URL-decode əvvəlcə (+49 → space+49 → adi ədəd kimi)
  let u;
  try { u = decodeURIComponent(url.replace(/\+/g, ' ')); } catch { u = url; }
  const patterns = [
    /@(-?\d+\.\d+),\s*(-?\d+\.\d+)/,               // @lat,lng (viewport)
    /\/search\/(-?\d+\.\d+),\s*\+?(-?\d+\.\d+)/,   // /search/lat,+lng  ← yeni
    /[?&]q=(-?\d+\.\d+),\s*\+?(-?\d+\.\d+)/,       // ?q=lat,lng
    /[?&]ll=(-?\d+\.\d+),\s*(-?\d+\.\d+)/,          // ?ll=lat,lng
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,               // data blob
    /place\/[^/]+\/@(-?\d+\.\d+),\s*(-?\d+\.\d+)/,  // place/@lat,lng
    /\/(-?\d{1,3}\.\d{4,}),\s*\+?(-?\d{1,3}\.\d{4,})/, // generic path (min 4 decimal)
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m) return { lat: m[1].trim(), lng: m[2].trim() };
  }
  return null;
}

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

async function resolveShortUrl(url) {
  if (!url.includes('goo.gl') && !url.includes('maps.app')) return url;
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/resolve-url`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON}`,
      },
      body:   JSON.stringify({ url }),
      signal: AbortSignal.timeout(10000),
    });
    const json = await res.json();
    if (json.finalUrl && json.finalUrl.includes('google.com/maps')) return json.finalUrl;
  } catch {}
  return url;
}

const AZ_CITIES = [
  'Bakı', 'Gəncə', 'Sumqayıt', 'Mingəçevir', 'Naxçıvan', 'Lənkəran', 'Şirvan', 'Şəki', 'Yevlax', 'Xankəndi',
  'Abşeron', 'Ağcabədi', 'Ağdam', 'Ağdaş', 'Ağstafa', 'Ağsu', 'Astara', 'Balakən', 'Bərdə', 'Beyləqan',
  'Biləsuvar', 'Cəbrayıl', 'Cəlilabad', 'Daşkəsən', 'Füzuli', 'Gədəbəy', 'Goranboy', 'Göyçay', 'Göygöl',
  'Hacıqabul', 'Hadrut', 'İmişli', 'İsmayıllı', 'Kəlbəcər', 'Kürdəmir', 'Laçın', 'Lerik', 'Masallı', 'Neftçala',
  'Oğuz', 'Qax', 'Qazax', 'Qəbələ', 'Qobustan', 'Quba', 'Qubadlı', 'Qusar', 'Saatlı', 'Sabirabad',
  'Salyan', 'Samux', 'Siyəzən', 'Şamaxı', 'Şəmkir', 'Şuşa', 'Tartar', 'Tovuz', 'Ucar', 'Xaçmaz',
  'Xızı', 'Xocavənd', 'Yardımlı', 'Zaqatala', 'Zəngilan', 'Zərdab',
  'Babək', 'Culfa', 'Kəngərli', 'Ordubad', 'Sədərək', 'Şahbuz', 'Şərur',
];

const CUISINE_TAG_OPTIONS = [
  'Azərbaycan', 'Türk', 'İtalyan', 'Fransız', 'Çin', 'Yapon', 'Hind',
  'Avropa', 'Fusyon', 'Mərkəzi Asiya', 'Pizza', 'Burger', 'Sushi',
  'Kahve', 'Brunch', 'Vegan', 'Şirniyyat', 'Dəniz məhsulları', 'BBQ',
];

const FEATURE_OPTIONS = [
  // App kateqoriyaları
  'Səhər yeməyi', 'Brunch',       'Açıq terras',  'Canlı musiqi', 'Pet Friendly',
  'Vegan',        'Sushi',        'Namaz otağı',
  // Əlavə xüsusiyyətlər
  'WiFi',         'Parking',      'Çatdırılma',   'Rezervasiya',
  'Uşaq menyu',  'Halal',        'Şəxsi otaqlar','Kart ilə ödəniş',
  'Siqaret qadağan', 'Valet parking', 'Sürücü xidməti', 'İkram xidməti', 'Proyektor',
  'Ailə', 'Alkoqolsuz',
];

// Normalize file type and extension — fixes .jfif, .webp, unknown types
function getUploadMeta(file) {
  const rawType = file.type || '';
  const name = file.name.toLowerCase();
  let contentType = 'image/jpeg';
  let ext = 'jpg';
  if (rawType === 'image/png' || name.endsWith('.png')) { contentType = 'image/png'; ext = 'png'; }
  else if (rawType === 'image/webp' || name.endsWith('.webp')) { contentType = 'image/webp'; ext = 'webp'; }
  else if (rawType === 'image/gif' || name.endsWith('.gif')) { contentType = 'image/gif'; ext = 'gif'; }
  return { contentType, ext };
}

const ProfilePage = () => {
  const { business, logout, refreshBusiness } = useAuth();
  const { isOwner, activeLocation, refreshLocations, isAdminView } = useLocation();

  // Admin mode: chain owner şirkət profilini görür
  // Branch mode: chain owner seçilmiş filialın profilini görür
  // Manager: öz filialının profilini görür
  const isBranchMode = isOwner && business?.is_chain && !isAdminView;
  const profile = isBranchMode ? (activeLocation || business)
                : isOwner      ? business
                :                (activeLocation || business);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [chainEditing, setChainEditing] = useState(false);
  const [chainSaving, setChainSaving] = useState(false);
  const [chainForm, setChainForm] = useState({
    name:                business?.name || '',
    phone:               business?.phone ? (business.phone.startsWith('+') ? business.phone : '+' + business.phone) : '+',
    voen:                business?.voen || '',
    representative_name: business?.representative_name || '',
    city:                business?.city || 'Bakı',
  });

  const logoInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const branchLogoInputRef = useRef(null);
  const branchPhotosInputRef = useRef(null);

  const [locationMode, setLocationMode] = useState('link'); // 'gps' | 'link'
  const [gpsStatus, setGpsStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [gpsError, setGpsError] = useState('');

  const [formData, setFormData] = useState({
    name: profile?.name || '',
    phone: profile?.phone ? (profile.phone.startsWith('+') ? profile.phone : '+' + profile.phone) : '+',
    notification_whatsapp: profile?.notification_whatsapp ? (profile.notification_whatsapp.startsWith('+') ? profile.notification_whatsapp : '+' + profile.notification_whatsapp) : '+',
    address: profile?.address || '',
    city: profile?.city || 'Bakı',

    description: profile?.description || '',
    description_en: profile?.description_en || '',
    maps_url: profile?.maps_url || '',
    instagram_url: profile?.instagram_url || '',
    service_fee: profile?.service_fee != null ? String(profile.service_fee) : '',
    service_fee_type: profile?.service_fee_type || 'percent',
    latitude: profile?.latitude?.toString() || '',
    longitude: profile?.longitude?.toString() || '',
    target_audience: profile?.target_audience || [],
    cuisine_tags: Array.isArray(profile?.cuisine_tags) ? profile.cuisine_tags : [],
    is_tourist_friendly: profile?.is_tourist_friendly || false,
    is_pet_friendly: profile?.is_pet_friendly || false,
    is_corporate_friendly: profile?.is_corporate_friendly || false,
    corporate_discount_percent: profile?.corporate_discount_percent != null ? String(profile.corporate_discount_percent) : '',
    features: Array.isArray(profile?.features) ? profile.features : [],
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [deliverySettings, setDeliverySettings] = useState({
    delivery_enabled: false,
    delivery_mode: 'own_courier',
    delivery_fee_model: 'base_plus_per_km',
    delivery_radius_km: '5',
    delivery_free_radius_km: '5',
    delivery_fee_base: '2',
    delivery_fee_per_km: '0.5',
    delivery_min_order: '0',
  });
  const [savingDelivery, setSavingDelivery] = useState(false);

  useEffect(() => {
    if (!activeLocation) return;
    setDeliverySettings({
      delivery_enabled: activeLocation.delivery_enabled || false,
      delivery_mode: activeLocation.delivery_mode || 'own_courier',
      delivery_fee_model: activeLocation.delivery_fee_model || 'base_plus_per_km',
      delivery_radius_km: activeLocation.delivery_radius_km?.toString() || '5',
      delivery_free_radius_km: activeLocation.delivery_free_radius_km?.toString() || '5',
      delivery_fee_base: activeLocation.delivery_fee_base?.toString() || '2',
      delivery_fee_per_km: activeLocation.delivery_fee_per_km?.toString() || '0.5',
      delivery_min_order: activeLocation.delivery_min_order?.toString() || '0',
    });
  }, [activeLocation?.id]);

  const handleDeliverySave = async () => {
    if (!activeLocation) return;
    setSavingDelivery(true);
    try {
      // platform_pool həmişə base_plus_per_km istifadə edir — UI seçici göstərmir,
      // amma RPC-nin gözlədiyi kimi sahə tutarlı qalsın deyə burda təyin edilir
      const effectiveFeeModel = deliverySettings.delivery_mode === 'platform_pool'
        ? 'base_plus_per_km' : deliverySettings.delivery_fee_model;

      const { error } = await supabase.from('business_locations').update({
        delivery_enabled: deliverySettings.delivery_enabled,
        delivery_mode: deliverySettings.delivery_mode,
        delivery_fee_model: effectiveFeeModel,
        delivery_radius_km: parseFloat(deliverySettings.delivery_radius_km) || 5,
        delivery_free_radius_km: parseFloat(deliverySettings.delivery_free_radius_km) || 5,
        delivery_fee_base: parseFloat(deliverySettings.delivery_fee_base) || 0,
        delivery_fee_per_km: parseFloat(deliverySettings.delivery_fee_per_km) || 0,
        delivery_min_order: parseFloat(deliverySettings.delivery_min_order) || 0,
      }).eq('id', activeLocation.id);
      if (error) throw error;
      await refreshLocations();
      alert('Çatdırılma ayarları yadda saxlanıldı');
    } catch (err) {
      alert('Xəta: ' + err.message);
    } finally {
      setSavingDelivery(false);
    }
  };

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleGetGPS = () => {
    if (!navigator.geolocation) {
      setGpsError('Bu brauzer GPS dəstəkləmir.');
      setGpsStatus('error');
      return;
    }
    setGpsStatus('loading');
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng, maps_url: mapsUrl }));
        setGpsStatus('success');
      },
      (err) => {
        const msgs = {
          1: 'İcazə verilmədi. Brauzer ayarlarından yerə icazə verin.',
          2: 'Yer məlumatı alına bilmədi.',
          3: 'Vaxt aşıldı. Yenidən cəhd edin.',
        };
        setGpsError(msgs[err.code] || 'Xəta baş verdi.');
        setGpsStatus('error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const toggleAudience = (id) => {
    setFormData(prev => ({
      ...prev,
      target_audience: prev.target_audience.includes(id)
        ? prev.target_audience.filter(a => a !== id)
        : [...prev.target_audience, id],
    }));
  };

  const toggleFeature = (f) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(f)
        ? prev.features.filter(x => x !== f)
        : [...prev.features, f],
    }));
  };

  const toggleCuisineTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      cuisine_tags: prev.cuisine_tags.includes(tag)
        ? prev.cuisine_tags.filter(t => t !== tag)
        : [...prev.cuisine_tags, tag],
    }));
  };

  // ── Upload Logo ──────────────────────────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const { contentType, ext } = getUploadMeta(compressed);
      const path = `logos/${business.id}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { upsert: true, contentType });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      const freshUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { data: updResult, error: dbErr } = await supabase
        .from('businesses').update({ image_url: freshUrl }).eq('id', business.id).select('id').single();
      if (dbErr || !updResult) {
        // RLS owner_id uyğunsuzluğu — email üzrə fallback
        const { error: dbErr2 } = await supabase
          .from('businesses').update({ image_url: freshUrl }).eq('email', business.email);
        if (dbErr2) throw dbErr2;
      }
      await refreshBusiness();
      if (logoInputRef.current) logoInputRef.current.value = '';
      showMsg('✅ Logo yükləndi!');
    } catch (err) {
      showMsg('❌ Logo xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Ana şəkili silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('businesses').update({ image_url: null }).eq('id', business.id);
      if (error) throw error;
      await refreshBusiness();
      showMsg('✅ Ana şəkil silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Upload Additional Photos ─────────────────────────────────────────────
  const PHOTO_LIMITS = { 0: 10, 1: 10, 2: 30, 3: Infinity };
  const photoLimit = PHOTO_LIMITS[business?.premium_tier ?? 0];

  const handlePhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existing = business.photos || [];
    const remaining = photoLimit - existing.length;
    if (remaining <= 0) {
      showMsg(`❌ Foto limitinə çatdınız (maks. ${photoLimit}). Planı yüksəldin.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (toUpload.length < files.length) {
      showMsg(`⚠️ Yalnız ${toUpload.length} şəkil yüklənəcək (limit: ${photoLimit})`);
    }
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of toUpload) {
        const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
        const { contentType, ext } = getUploadMeta(compressed);
        const path = `photos/${business.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('business-photos')
          .upload(path, compressed, { contentType });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
      const { error: dbErr } = await supabase
        .from('businesses').update({ photos: [...existing, ...newUrls] }).eq('id', business.id);
      if (dbErr) throw dbErr;
      await refreshBusiness();
      showMsg(`✅ ${newUrls.length} şəkil yükləndi!`);
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (url) => {
    if (!window.confirm('Bu şəkili silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const updated = (business.photos || []).filter(p => p !== url);
      const { error } = await supabase.from('businesses').update({ photos: updated }).eq('id', business.id);
      if (error) throw error;
      await refreshBusiness();
      showMsg('✅ Şəkil silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Branch Image Upload (manager) ────────────────────────────────────────
  const branchPhotoLimit = { 0: 10, 1: 10, 2: 30, 3: Infinity }[activeLocation?.premium_tier ?? 0];

  const handleBranchLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeLocation) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const { contentType, ext } = getUploadMeta(compressed);
      const path = `locations/${activeLocation.id}/cover.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { upsert: true, contentType });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      const freshUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { error: dbErr } = await supabase
        .from('business_locations').update({ image_url: freshUrl }).eq('id', activeLocation.id);
      if (dbErr) throw dbErr;
      await refreshLocations();
      if (branchLogoInputRef.current) branchLogoInputRef.current.value = '';
      showMsg('✅ Filial şəkli yükləndi!');
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBranchLogo = async () => {
    if (!window.confirm('Filial əsas şəklini silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('business_locations').update({ image_url: null }).eq('id', activeLocation.id);
      if (error) throw error;
      await refreshLocations();
      showMsg('✅ Filial şəkli silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBranchPhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeLocation) return;
    const existing = activeLocation.photos || [];
    const remaining = branchPhotoLimit - existing.length;
    if (remaining <= 0) {
      showMsg(`❌ Foto limitinə çatdınız (maks. ${branchPhotoLimit})`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of toUpload) {
        const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
        const { contentType, ext } = getUploadMeta(compressed);
        const path = `locations/${activeLocation.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('business-photos')
          .upload(path, compressed, { contentType });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
      const { error: dbErr } = await supabase
        .from('business_locations').update({ photos: [...existing, ...newUrls] }).eq('id', activeLocation.id);
      if (dbErr) throw dbErr;
      await refreshLocations();
      showMsg(`✅ ${newUrls.length} şəkil yükləndi!`);
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleBranchRemovePhoto = async (url) => {
    if (!window.confirm('Bu şəkili silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const updated = (activeLocation.photos || []).filter(p => p !== url);
      const { error } = await supabase.from('business_locations').update({ photos: updated }).eq('id', activeLocation.id);
      if (error) throw error;
      await refreshLocations();
      showMsg('✅ Şəkil silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Save Profile ─────────────────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,

        description: formData.description,
        description_en: formData.description_en,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        maps_url: formData.maps_url || null,
        instagram_url: formData.instagram_url || null,
        service_fee: formData.service_fee !== '' ? parseFloat(formData.service_fee) : null,
        service_fee_type: formData.service_fee !== '' ? formData.service_fee_type : null,
        features: formData.features,
        cuisine_tags: formData.cuisine_tags,
        target_audience: formData.target_audience,
        is_tourist_friendly: formData.is_tourist_friendly,
        is_pet_friendly: formData.is_pet_friendly,
        is_corporate_friendly: formData.is_corporate_friendly,
        corporate_discount_percent: formData.is_corporate_friendly && formData.corporate_discount_percent !== ''
          ? parseFloat(formData.corporate_discount_percent) : 0,
        notification_whatsapp: formData.notification_whatsapp || null,
        updated_at: new Date().toISOString(),
      };

      if (isOwner && !isBranchMode) {
        // Admin mode: businesses cədvəlini yenilə
        const { error } = await supabase.from('businesses').update({
          ...payload,
          target_audience: formData.target_audience,
        }).eq('id', business.id);
        if (error) throw error;
        await refreshBusiness();
      } else {
        // Branch mode (owner) və ya manager → seçilmiş filialı yenilə
        const { updated_at: _skip, ...locationPayload } = payload;
        const targetId = activeLocation?.id;
        if (!targetId) throw new Error('Filial seçilməyib');
        const { error } = await supabase.from('business_locations')
          .update(locationPayload).eq('id', targetId);
        if (error) throw error;
        await refreshLocations();
      }

      showMsg('✅ Profil yeniləndi!');
      setEditing(false);
    } catch (err) {
      showMsg('❌ Xəta: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Change Password ──────────────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMsg('❌ Şifrələr uyğun gəlmir');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showMsg('❌ Şifrə ən azı 6 simvol olmalıdır');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) throw error;
      showMsg('✅ Şifrə dəyişdirildi!');
      setChangingPassword(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  // ── Admin mode: yalnız şirkət məlumatları ───────────────────────────────
  if (isOwner && business?.is_chain && isAdminView) {
    const saveChain = async (e) => {
      e.preventDefault();
      if (!chainForm.name.trim()) { showMsg('❌ Şirkət adı tələb olunur'); return; }
      setChainSaving(true);
      const { error } = await supabase.from('businesses').update({
        name:                chainForm.name.trim(),
        phone:               chainForm.phone.trim() || null,
        voen:                chainForm.voen.trim() || null,
        representative_name: chainForm.representative_name.trim() || null,
        city:                chainForm.city,
      }).eq('id', business.id);
      setChainSaving(false);
      if (error) { showMsg('❌ ' + error.message); return; }
      await refreshBusiness();
      showMsg('✅ Şirkət məlumatları yeniləndi');
      setChainEditing(false);
    };

    return (
      <div className="profile-page">
        <div className="profile-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0 }}>🏢 Şirkət Profili</h1>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
              color: '#fff', fontWeight: 700, fontSize: 12,
              padding: '4px 12px', borderRadius: 20,
            }}>Filial Şəbəkəsi</span>
          </div>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
            Filialların profilini hər filialın meneceri öz hesabından idarə edir
          </p>
        </div>

        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>{message}</div>
        )}

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#111' }}>Şirkət Məlumatları</h3>
            {!chainEditing && (
              <button onClick={() => setChainEditing(true)} style={cs.editBtn}>✏️ Düzəliş Et</button>
            )}
          </div>

          {!chainEditing ? (
            <div style={{ display: 'grid', gap: 14 }}>
              {[
                { label: 'Şirkət adı',  value: business?.name },
                { label: 'Email',       value: business?.email },
                { label: 'Telefon',     value: business?.phone || '—' },
                { label: 'VÖEN',        value: business?.voen || '—' },
                { label: 'Nümayəndə',  value: business?.representative_name || '—' },
                { label: 'Şəhər',       value: business?.city || '—' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', gap: 8, fontSize: 14, borderBottom: '1px solid #f5f5f5', paddingBottom: 10 }}>
                  <span style={{ color: '#6b7280', minWidth: 130 }}>{row.label}</span>
                  <span style={{ color: '#111', fontWeight: 500 }}>{row.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <form onSubmit={saveChain}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ ...cs.field, gridColumn: '1 / -1' }}>
                  <label style={cs.label}>Şirkət Adı *</label>
                  <input style={cs.input} value={chainForm.name}
                    onChange={e => setChainForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div style={cs.field}>
                  <label style={cs.label}>Telefon</label>
                  <input style={cs.input} value={chainForm.phone} placeholder="+994 50 000 00 00"
                    onChange={e => { const v = e.target.value; setChainForm(p => ({ ...p, phone: v.startsWith('+') ? v : '+' + v.replace(/^\+*/, '') })); }} />
                </div>
                <div style={cs.field}>
                  <label style={cs.label}>VÖEN</label>
                  <input style={cs.input} value={chainForm.voen} placeholder="1234567890"
                    onChange={e => setChainForm(p => ({ ...p, voen: e.target.value }))} />
                </div>
                <div style={cs.field}>
                  <label style={cs.label}>Nümayəndə</label>
                  <input style={cs.input} value={chainForm.representative_name} placeholder="Ad Soyad"
                    onChange={e => setChainForm(p => ({ ...p, representative_name: e.target.value }))} />
                </div>
                <div style={cs.field}>
                  <label style={cs.label}>Şəhər</label>
                  <select style={cs.input} value={chainForm.city}
                    onChange={e => setChainForm(p => ({ ...p, city: e.target.value }))}>
                    {AZ_CITIES.map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" style={cs.cancelBtn} onClick={() => setChainEditing(false)}>Ləğv Et</button>
                <button type="submit" style={cs.saveBtn} disabled={chainSaving}>
                  {chainSaving ? 'Saxlanır...' : '✓ Yadda Saxla'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Şifrə dəyişikliyi */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#111' }}>🔒 Şifrə Dəyişikliyi</h3>
          {!changingPassword ? (
            <button className="change-password-button" onClick={() => setChangingPassword(true)}>🔑 Şifrə Dəyiş</button>
          ) : (
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Yeni Şifrə</label>
                <input type="password" value={passwordData.newPassword}
                  onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="••••••••" minLength={6} required />
              </div>
              <div className="form-group">
                <label>Yeni Şifrə Təkrar</label>
                <input type="password" value={passwordData.confirmPassword}
                  onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="••••••••" required />
              </div>
              <div className="form-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Dəyişdirilir...' : '💾 Şifrəni Dəyiş'}
                </button>
                <button type="button" className="cancel-btn"
                  onClick={() => setChangingPassword(false)}>❌ Ləğv Et</button>
              </div>
            </form>
          )}
        </div>

        <button
          onClick={logout}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: '#fee2e2', color: '#b91c1c',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}
        >
          🚪 Çıxış
        </button>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
          {(profile?.image_url || business?.image_url) ? (
            <img
              src={profile?.image_url || business?.image_url}
              alt="logo"
              style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: '2px solid #f0f0f5' }}
            />
          ) : (
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #7B2FF7, #E63950)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}>🏪</div>
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: 22, color: '#1a1a2e' }}>{profile?.name || 'Biznes Profili'}</h1>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
              {isBranchMode ? `${activeLocation?.name || 'Filial'} — filial məlumatları` : 'Şirkət məlumatlarınızı idarə edin'}
            </p>
          </div>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>{message}</div>
      )}

      {/* ── Şəkillər — filial (manager və ya branch mode owner) ── */}
      {(!isOwner || isBranchMode) && activeLocation && (
        <div className="profile-card">
          <h2>📸 Filial Şəkilləri</h2>

          <div style={{ marginBottom: 24 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Əsas Şəkil — App kartında görünür</p>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Mobile appda bu filialın kart şəkli kimi görünür.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                width: 280, height: 130, borderRadius: 16, overflow: 'hidden',
                position: 'relative', background: '#1a1a2e', border: '2px solid #eee',
              }}>
                {activeLocation.image_url ? (
                  <img key={activeLocation.image_url} src={thumbUrl(activeLocation.image_url)} alt="branch preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    background: 'linear-gradient(135deg, #7B2FF7, #E63950)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 40,
                  }}>🏪</div>
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  padding: '20px 12px 10px', color: '#fff',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{activeLocation.name || 'Filial Adı'}</div>
                </div>
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.5)', borderRadius: 20,
                  padding: '3px 8px', fontSize: 10, color: '#fff',
                }}>Önizləmə</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button className="edit-button" onClick={() => branchLogoInputRef.current?.click()} disabled={uploading || loading}>
                  {uploading ? 'Yüklənir...' : '📤 Şəkil Yüklə'}
                </button>
                {activeLocation.image_url && (
                  <button
                    onClick={handleDeleteBranchLogo}
                    disabled={uploading || loading}
                    style={{
                      background: '#e74c3c', color: '#fff', border: 'none',
                      borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, opacity: (uploading || loading) ? 0.6 : 1,
                    }}
                  >
                    🗑️ Şəkili Sil
                  </button>
                )}
                {activeLocation.image_url
                  ? <span style={{ fontSize: 12, color: '#27ae60' }}>✅ Şəkil var</span>
                  : <span style={{ fontSize: 12, color: '#f39c12' }}>⚠️ Şəkil yoxdur</span>
                }
              </div>
              <input ref={branchLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleBranchLogoUpload} />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <p style={{ fontWeight: 600, margin: 0 }}>Əlavə Şəkillər</p>
              <span style={{
                fontSize: 12,
                color: (activeLocation.photos || []).length >= branchPhotoLimit ? '#e74c3c' : '#888',
                background: '#f5f5f5', borderRadius: 6, padding: '2px 8px',
              }}>
                {(activeLocation.photos || []).length} / {branchPhotoLimit === Infinity ? '∞' : branchPhotoLimit}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              {(activeLocation.photos || []).map((url, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={thumbUrl(url)} alt={`photo-${i}`}
                    style={{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
                  <button onClick={() => handleBranchRemovePhoto(url)}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                      border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ))}
            </div>
            <button
              className="edit-button"
              onClick={() => branchPhotosInputRef.current?.click()}
              disabled={uploading || (activeLocation.photos || []).length >= branchPhotoLimit}
            >
              {uploading ? 'Yüklənir...' : (activeLocation.photos || []).length >= branchPhotoLimit
                ? `🔒 Limit dolub (${branchPhotoLimit})`
                : '📷 Şəkil Əlavə Et'}
            </button>
            <input ref={branchPhotosInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={handleBranchPhotosUpload} />
          </div>
        </div>
      )}

      {/* ── Şəkillər — yalnız admin mode owner ── */}
      {isOwner && !isBranchMode && <div className="profile-card">
        <h2>📸 Şəkillər</h2>

        <div style={{ marginBottom: 24 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>Ana Şəkil — App kartında görünür</p>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Bu şəkil mobile appda kafe kartının arxa planı kimi görünür. Geniş format (landscape) tövsiyə olunur.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Card preview */}
            <div style={{
              width: 280, height: 130, borderRadius: 16, overflow: 'hidden',
              position: 'relative', background: '#1a1a2e', border: '2px solid #eee',
            }}>
              {business?.image_url ? (
                <img key={business.image_url} src={thumbUrl(business.image_url)} alt="card preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  background: 'linear-gradient(135deg, #7B2FF7, #E63950)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 40,
                }}>🏪</div>
              )}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                padding: '20px 12px 10px',
                color: '#fff',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{business?.name || 'Kafe Adı'}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{business?.cuisine_type || 'Mətbəx növü'}</div>
              </div>
              <div style={{
                position: 'absolute', top: 8, right: 8,
                background: 'rgba(0,0,0,0.5)', borderRadius: 20,
                padding: '3px 8px', fontSize: 10, color: '#fff',
              }}>Önizləmə</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="edit-button" onClick={() => logoInputRef.current?.click()} disabled={uploading || loading}>
                {uploading ? 'Yüklənir...' : '📤 Şəkil Yüklə'}
              </button>
              {business?.image_url && (
                <button
                  onClick={handleDeleteLogo}
                  disabled={uploading || loading}
                  style={{
                    background: '#e74c3c', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, opacity: (uploading || loading) ? 0.6 : 1,
                  }}
                >
                  🗑️ Şəkili Sil
                </button>
              )}
              {business?.image_url && (
                <span style={{ fontSize: 12, color: '#27ae60' }}>✅ Şəkil var</span>
              )}
              {!business?.image_url && (
                <span style={{ fontSize: 12, color: '#f39c12' }}>⚠️ Şəkil yoxdur — gradient göstəriləcək</span>
              )}
            </div>
            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={handleLogoUpload} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <p style={{ fontWeight: 600, margin: 0 }}>Əlavə Şəkillər</p>
            <span style={{
              fontSize: 12, color: (business?.photos || []).length >= photoLimit ? '#e74c3c' : '#888',
              background: '#f5f5f5', borderRadius: 6, padding: '2px 8px',
            }}>
              {(business?.photos || []).length} / {photoLimit === Infinity ? '∞' : photoLimit}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            {(business?.photos || []).map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={thumbUrl(url)} alt={`photo-${i}`}
                  style={{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
                <button onClick={() => handleRemovePhoto(url)}
                  style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                    border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ))}
          </div>
          <button
            className="edit-button"
            onClick={() => photosInputRef.current?.click()}
            disabled={uploading || (business?.photos || []).length >= photoLimit}
            title={(business?.photos || []).length >= photoLimit ? `Limit: ${photoLimit} şəkil` : ''}
          >
            {uploading ? 'Yüklənir...' : (business?.photos || []).length >= photoLimit ? `🔒 Limit dolub (${photoLimit})` : '📷 Şəkil Əlavə Et'}
          </button>
          <input ref={photosInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={handlePhotosUpload} />
        </div>
      </div>}

      {/* ── Əsas Məlumatlar ── */}
      <div className="profile-card">
        <div className="profile-info-section">
          <h2>📋 Əsas Məlumatlar</h2>

          {!editing ? (
            <div className="info-display">
              {[
                { label: 'Ad', value: profile?.name },
                { label: 'Email', value: isOwner ? business?.email : profile?.user_email || business?.email },
                { label: 'Telefon', value: profile?.phone },
                { label: 'Ünvan', value: profile?.address },
                { label: 'Şəhər', value: profile?.city },

                { label: 'Instagram', value: profile?.instagram_url || '—' },
                {
                  label: 'Servis haqqı',
                  value: profile?.service_fee != null
                    ? (profile.service_fee_type === 'fixed'
                        ? `${profile.service_fee} ₼ (sabit)`
                        : `${profile.service_fee}% (faiz)`)
                    : '—',
                },
                {
                  label: 'Koordinat',
                  value: profile?.latitude && profile?.longitude
                    ? `${profile.latitude}, ${profile.longitude}`
                    : '—',
                },
                ...(isOwner ? [{
                  label: 'Hədəf kütlə',
                  value: (business?.target_audience || []).length > 0
                    ? TARGET_OPTIONS.filter(o => (business.target_audience || []).includes(o.id)).map(o => o.label).join(' · ')
                    : '—',
                }] : []),
              ].map(item => (
                <div key={item.label} className="info-item">
                  <span className="info-label">{item.label}:</span>
                  <span className="info-value">{item.value}</span>
                </div>
              ))}

              {/* Cuisine tags display */}
              {(Array.isArray(profile?.cuisine_tags) ? profile.cuisine_tags : []).length > 0 && (
                <div className="info-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="info-label" style={{ marginBottom: 8 }}>Mətbəx növləri:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.cuisine_tags.map(t => (
                      <span key={t} style={{
                        background: '#fff7ed', color: '#c2410c', fontSize: 12,
                        fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                        border: '1px solid #fed7aa',
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pet friendly badge */}
              {profile?.is_pet_friendly && (
                <div className="info-item">
                  <span className="info-label">Pet dostu:</span>
                  <span style={{
                    background: '#fef3c7', color: '#92400e',
                    padding: '2px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                  }}>🐾 Pet-Friendly</span>
                </div>
              )}


              {/* Features display */}
              {(Array.isArray(profile?.features) ? profile.features : []).length > 0 && (
                <div className="info-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="info-label" style={{ marginBottom: 8 }}>Xüsusiyyətlər:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.features.map(f => (
                      <span key={f} style={{
                        background: '#f0f4ff', color: '#667eea', fontSize: 12,
                        fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                        border: '1px solid #c7d2fe',
                      }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}

              <button className="edit-button" onClick={() => setEditing(true)}>✏️ Düzəliş Et</button>
            </div>
          ) : (
            <form onSubmit={handleEditSubmit} className="edit-form">
              <div className="form-group">
                <label>Kafe Adı</label>
                <input type="text" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input type="tel" value={formData.phone} placeholder="+994501234567"
                  onChange={e => { const v = e.target.value; setFormData({ ...formData, phone: v.startsWith('+') ? v : '+' + v.replace(/^\+*/, '') }); }} />
              </div>
              <div className="form-group">
                <label>📱 WhatsApp Bildiriş Nömrəsi</label>
                <input type="tel" value={formData.notification_whatsapp}
                  placeholder="+994501234567"
                  onChange={e => { const v = e.target.value; setFormData({ ...formData, notification_whatsapp: v.startsWith('+') ? v : '+' + v.replace(/^\+*/, '') }); }} />
                <small style={{ color: '#94A3B8', fontSize: 12 }}>Yeni rezervasiyalar bu nömrəyə WhatsApp mesajı göndəriləcək</small>
              </div>
              <div className="form-group">
                <label>Ünvan</label>
                <input type="text" value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Şəhər</label>
                <select value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}>
                  {AZ_CITIES.map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {/* Location — 2 seçim: GPS və ya Link */}
              <div className="form-group">
                <label>📍 Kafenin Yeri</label>

                {/* Mode seçim düymələri */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => { setLocationMode('gps'); setGpsStatus('idle'); setGpsError(''); }}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: locationMode === 'gps' ? '#4285F4' : '#e0e0e0',
                      background: locationMode === 'gps' ? '#EBF3FF' : '#fafafa',
                      color: locationMode === 'gps' ? '#1a73e8' : '#555',
                      fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    📱 İndi kafədəyəm
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLocationMode('link'); setGpsStatus('idle'); setGpsError(''); }}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: locationMode === 'link' ? '#4285F4' : '#e0e0e0',
                      background: locationMode === 'link' ? '#EBF3FF' : '#fafafa',
                      color: locationMode === 'link' ? '#1a73e8' : '#555',
                      fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    🔗 Linki yapışdır
                  </button>
                </div>

                {/* GPS rejimi */}
                {locationMode === 'gps' && (
                  <div>
                    <button
                      type="button"
                      onClick={handleGetGPS}
                      disabled={gpsStatus === 'loading'}
                      style={{
                        width: '100%', padding: '12px', borderRadius: 8,
                        background: gpsStatus === 'loading' ? '#ccc' : '#34A853',
                        color: '#fff', fontWeight: 700, fontSize: 14,
                        border: 'none', cursor: gpsStatus === 'loading' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {gpsStatus === 'loading' ? '⏳ Yer alınır...' : '📍 Hazırki Yeri Al'}
                    </button>
                    {gpsStatus === 'success' && formData.latitude && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #86EFAC' }}>
                        <span style={{ color: '#16A34A', fontWeight: 700, fontSize: 13 }}>
                          ✓ Koordinatlar alındı: {formData.latitude}, {formData.longitude}
                        </span>
                      </div>
                    )}
                    {gpsStatus === 'error' && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FCA5A5' }}>
                        <span style={{ color: '#DC2626', fontSize: 13 }}>⚠️ {gpsError}</span>
                      </div>
                    )}
                    <small style={{ color: '#888', marginTop: 6, display: 'block' }}>
                      Kafenin içindəykən düyməyə basın. Brauzer yer icazəsi istəyəcək — icazə verin.
                    </small>
                  </div>
                )}

                {/* Link rejimi */}
                {locationMode === 'link' && (
                  <div>
                    <input
                      type="url"
                      value={formData.maps_url}
                      placeholder="https://maps.app.goo.gl/... və ya google.com/maps linki"
                      onChange={async e => {
                        const url = e.target.value;
                        setFormData(prev => ({ ...prev, maps_url: url, latitude: '', longitude: '' }));
                        if (!url) return;
                        let resolved = url;
                        if (url.includes('goo.gl') || url.includes('maps.app')) {
                          resolved = await resolveShortUrl(url);
                        }
                        const coords = parseGoogleMapsCoords(resolved);
                        if (coords) {
                          setFormData(prev => ({ ...prev, maps_url: url, latitude: coords.lat, longitude: coords.lng }));
                        }
                      }}
                    />
                    {formData.maps_url && formData.latitude && formData.longitude && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #86EFAC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: '#16A34A', fontWeight: 700, fontSize: 13 }}>
                          ✓ Koordinatlar tapıldı: {formData.latitude}, {formData.longitude}
                        </span>
                        <a
                          href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: '#1a73e8', fontWeight: 600, textDecoration: 'none' }}
                        >
                          Xəritədə gör →
                        </a>
                      </div>
                    )}
                    {formData.maps_url && !formData.latitude && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FCD34D' }}>
                        <span style={{ color: '#92400E', fontSize: 13 }}>⚠️ Koordinat tapılmadı. Google Maps → Paylaş → linki kopyalayın.</span>
                      </div>
                    )}
                    <small style={{ color: '#888', marginTop: 6, display: 'block' }}>
                      Google Maps-də kafenizi tapın → "Paylaş" → linki bura paste edin. Koordinatlar avtomatik çıxarılacaq.
                    </small>
                  </div>
                )}

                {/* Hazırda saxlanılmış koordinat göstər (hər iki rejimdə) */}
                {formData.latitude && formData.longitude && gpsStatus !== 'success' && locationMode === 'gps' && (
                  <div style={{ marginTop: 8, padding: '8px 14px', background: '#F8F9FA', borderRadius: 8, fontSize: 12, color: '#666' }}>
                    Saxlanılmış: {formData.latitude}, {formData.longitude}
                  </div>
                )}
              </div>

              {/* Instagram */}
              <div className="form-group">
                <label>📸 Instagram</label>
                <input
                  type="text"
                  value={formData.instagram_url}
                  placeholder="https://instagram.com/kafenizin_adi"
                  onChange={e => setFormData({ ...formData, instagram_url: e.target.value })}
                />
                <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                  Instagram profilinizin tam linkini daxil edin.
                </small>
              </div>

              {/* Servis haqqı */}
              <div className="form-group">
                <label>🧾 Servis haqqı</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.service_fee}
                    placeholder={formData.service_fee_type === 'percent' ? 'Məs: 10' : 'Məs: 5.00'}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === '' || /^\d*\.?\d*$/.test(v)) {
                        setFormData({ ...formData, service_fee: v });
                      }
                    }}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <select
                    value={formData.service_fee_type}
                    onChange={e => setFormData({ ...formData, service_fee_type: e.target.value })}
                    style={{ width: 130, flexShrink: 0 }}
                  >
                    <option value="percent">% faiz</option>
                    <option value="fixed">₼ sabit</option>
                  </select>
                </div>
                <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                  Boş qoyarsanız servis haqqı göstərilməyəcək. Müştərilər rezervasiya zamanı görəcək.
                </small>
              </div>

              {/* Location — manual coords */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Enlik (Latitude)</label>
                  <input type="number" step="any" value={formData.latitude} placeholder="40.4093"
                    onChange={e => setFormData({ ...formData, latitude: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Uzunluq (Longitude)</label>
                  <input type="number" step="any" value={formData.longitude} placeholder="49.8671"
                    onChange={e => setFormData({ ...formData, longitude: e.target.value })} />
                </div>
              </div>
              {formData.latitude && formData.longitude && (
                <small style={{ color: '#22c55e', display: 'block', marginBottom: 16, fontWeight: 600 }}>
                  ✅ Koordinat tapıldı: {parseFloat(formData.latitude).toFixed(5)}, {parseFloat(formData.longitude).toFixed(5)}
                </small>
              )}

              {/* Target Audience */}
              <div className="form-group">
                <label>Hədəf Kütlə (birdən çox seçilə bilər)</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                  {TARGET_OPTIONS.map(opt => (
                    <div key={opt.id}
                      onClick={() => toggleAudience(opt.id)}
                      style={{
                        padding: '10px 16px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                        border: formData.target_audience.includes(opt.id) ? '2px solid #667eea' : '2px solid #ddd',
                        background: formData.target_audience.includes(opt.id) ? '#f0f0ff' : '#fff',
                        transition: 'all 0.2s',
                      }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cuisine Tags */}
              <div className="form-group">
                <label>Mətbəx Növləri (birdən çox seçilə bilər)</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Turistlər bu teqlərə əsasən axtarış edir — azərbaycan mətbəxi seçmək xüsusilə tövsiyə olunur
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {CUISINE_TAG_OPTIONS.map(tag => {
                    const active = formData.cuisine_tags.includes(tag);
                    return (
                      <div key={tag}
                        onClick={() => toggleCuisineTag(tag)}
                        style={{
                          padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                          fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                          border: active ? '2px solid #ea580c' : '2px solid #e0e0e0',
                          background: active ? '#fff7ed' : '#fafafa',
                          color: active ? '#ea580c' : '#555',
                        }}>
                        {active ? '✓ ' : ''}{tag}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pet Friendly Toggle */}
              <div className="form-group">
                <label>Pet Dostu İşarəsi</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Aktiv edildikdə 🐾 badge ilə pet-friendly kafe kimi görünəcək
                </p>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, is_pet_friendly: !prev.is_pet_friendly }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    border: formData.is_pet_friendly ? '2px solid #d97706' : '2px solid #ddd',
                    background: formData.is_pet_friendly ? '#fffbeb' : '#fafafa',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: 44, height: 24, borderRadius: 12, position: 'relative',
                    background: formData.is_pet_friendly ? '#d97706' : '#d1d5db',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, left: formData.is_pet_friendly ? 23 : 3,
                      width: 18, height: 18, borderRadius: 9, background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: formData.is_pet_friendly ? '#d97706' : '#555' }}>
                    {formData.is_pet_friendly ? '🐾 Pet-Friendly aktiv' : 'Pet-Friendly deyil'}
                  </span>
                </div>
              </div>


              {/* Features / Amenities */}
              <div className="form-group">
                <label>Xüsusiyyətlər (birdən çox seçilə bilər)</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Kafenizin üstünlükləri — mobile appda müştərilərə göstəriləcək
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {FEATURE_OPTIONS.map(f => {
                    const active = formData.features.includes(f);
                    return (
                      <div key={f}
                        onClick={() => toggleFeature(f)}
                        style={{
                          padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                          fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                          border: active ? '2px solid #667eea' : '2px solid #e0e0e0',
                          background: active ? '#f0f0ff' : '#fafafa',
                          color: active ? '#667eea' : '#555',
                        }}>
                        {active ? '✓ ' : ''}{f}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tourist Friendly Toggle */}
              <div className="form-group">
                <label>Turist Dostu İşarəsi</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Aktiv edildikdə ✈️ badge ilə turist lentinə önə çıxacaq
                </p>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, is_tourist_friendly: !prev.is_tourist_friendly }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    border: formData.is_tourist_friendly ? '2px solid #16a34a' : '2px solid #ddd',
                    background: formData.is_tourist_friendly ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: 44, height: 24, borderRadius: 12, position: 'relative',
                    background: formData.is_tourist_friendly ? '#16a34a' : '#d1d5db',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, left: formData.is_tourist_friendly ? 23 : 3,
                      width: 18, height: 18, borderRadius: 9, background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: formData.is_tourist_friendly ? '#16a34a' : '#555' }}>
                    {formData.is_tourist_friendly ? '✈️ Turist-Friendly aktiv' : 'Turist-Friendly deyil'}
                  </span>
                </div>
              </div>

              {/* Korporativ Proqram */}
              <div className="form-group">
                <label>🏢 Korporativ Proqram</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Aktiv edildikdə korporativ işçilər bu kafedə xüsusi endirim alır
                </p>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, is_corporate_friendly: !prev.is_corporate_friendly }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    border: formData.is_corporate_friendly ? '2px solid #6C63FF' : '2px solid #ddd',
                    background: formData.is_corporate_friendly ? '#f5f3ff' : '#fafafa',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: 44, height: 24, borderRadius: 12, position: 'relative',
                    background: formData.is_corporate_friendly ? '#6C63FF' : '#d1d5db',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, left: formData.is_corporate_friendly ? 23 : 3,
                      width: 18, height: 18, borderRadius: 9, background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: formData.is_corporate_friendly ? '#6C63FF' : '#555' }}>
                    {formData.is_corporate_friendly ? '🏢 Korporativ Proqram aktiv' : 'Korporativ Proqram deyil'}
                  </span>
                </div>
                {formData.is_corporate_friendly && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                    <input
                      type="number"
                      min="0" max="100" step="1"
                      value={formData.corporate_discount_percent}
                      onChange={e => setFormData(prev => ({ ...prev, corporate_discount_percent: e.target.value }))}
                      placeholder="10"
                      style={{
                        width: 90, padding: '8px 12px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                        border: '2px solid #6C63FF', outline: 'none', textAlign: 'center',
                      }}
                    />
                    <span style={{ fontSize: 14, color: '#555', fontWeight: 500 }}>% endirim korporativ işçilərə</span>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Yadda saxlanılır...' : '💾 Yadda Saxla'}
                </button>
                <button type="button" className="cancel-btn"
                  onClick={() => setEditing(false)} disabled={loading}>❌ Ləğv Et</button>
              </div>
            </form>
          )}
        </div>
      </div>


      {/* ── Çatdırılma Ayarları ── */}
      <div className="profile-card">
        <div className="form-group">
          <label>🚴 Çatdırılma (Gəlib götür deyil — kuryer çatdırır)</label>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
            Aktiv edildikdə müştərilər app-dan sifariş verib evlərinə çatdırılma sifariş edə bilər
          </p>
          <div
            onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_enabled: !prev.delivery_enabled }))}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
              border: deliverySettings.delivery_enabled ? '2px solid #6C63FF' : '2px solid #ddd',
              background: deliverySettings.delivery_enabled ? '#f5f3ff' : '#fafafa',
              transition: 'all 0.2s',
            }}>
            <div style={{
              width: 44, height: 24, borderRadius: 12, position: 'relative',
              background: deliverySettings.delivery_enabled ? '#6C63FF' : '#d1d5db',
              transition: 'background 0.2s',
            }}>
              <div style={{
                position: 'absolute', top: 3, left: deliverySettings.delivery_enabled ? 23 : 3,
                width: 18, height: 18, borderRadius: 9, background: '#fff',
                transition: 'left 0.2s',
              }} />
            </div>
            <span style={{ fontWeight: 600, fontSize: 14, color: deliverySettings.delivery_enabled ? '#6C63FF' : '#555' }}>
              {deliverySettings.delivery_enabled ? '🚴 Çatdırılma aktiv' : 'Çatdırılma deaktiv'}
            </span>
          </div>

          {deliverySettings.delivery_enabled && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Kuryer rejimi</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { value: 'own_courier', label: '🧑 Öz kuryerim' },
                    { value: 'platform_pool', label: '🌐 Cafegoo hovuzu' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_mode: opt.value }))}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        border: deliverySettings.delivery_mode === opt.value ? '2px solid #6C63FF' : '2px solid #ddd',
                        background: deliverySettings.delivery_mode === opt.value ? '#f5f3ff' : '#fff',
                        color: deliverySettings.delivery_mode === opt.value ? '#6C63FF' : '#555',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {deliverySettings.delivery_mode === 'platform_pool' && (
                  <p style={{ fontSize: 11, color: '#a16207', marginTop: 6 }}>
                    ⚠️ Hovuz kuryer istifadə olunanda çatdırılma haqqından komissiya tutulur
                  </p>
                )}
              </div>

              {deliverySettings.delivery_mode === 'own_courier' && (
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Haqq modeli</span>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {[
                      { value: 'radius_only', label: '🆓 Yalnız radiusda, pulsuz' },
                      { value: 'free_then_per_km', label: '🆓➜💰 X km-ə qədər pulsuz, sonra km-başı' },
                      { value: 'base_plus_per_km', label: '💰 Baza haqqı + km-başı' },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_fee_model: opt.value }))}
                        style={{
                          padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          border: deliverySettings.delivery_fee_model === opt.value ? '2px solid #6C63FF' : '2px solid #ddd',
                          background: deliverySettings.delivery_fee_model === opt.value ? '#f5f3ff' : '#fff',
                          color: deliverySettings.delivery_fee_model === opt.value ? '#6C63FF' : '#555',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {(deliverySettings.delivery_mode === 'platform_pool' || deliverySettings.delivery_fee_model !== 'free_then_per_km') && (
                  <div>
                    <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
                      {deliverySettings.delivery_mode === 'own_courier' && deliverySettings.delivery_fee_model === 'radius_only' ? 'Pulsuz çatdırılma radiusu (km)' : 'Radius (km)'}
                    </span>
                    <input type="number" min="1" step="0.5" value={deliverySettings.delivery_radius_km}
                      onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_radius_km: e.target.value }))}
                      style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                  </div>
                )}

                {deliverySettings.delivery_mode === 'own_courier' && deliverySettings.delivery_fee_model === 'free_then_per_km' && (
                  <>
                    <div>
                      <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Pulsuz radius (km)</span>
                      <input type="number" min="0" step="0.5" value={deliverySettings.delivery_free_radius_km}
                        onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_free_radius_km: e.target.value }))}
                        style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                    </div>
                    <div>
                      <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Maksimum radius (km)</span>
                      <input type="number" min="0" step="0.5" value={deliverySettings.delivery_radius_km}
                        onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_radius_km: e.target.value }))}
                        style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                    </div>
                    <div>
                      <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Pulsuz radiusdan sonra ₼/km</span>
                      <input type="number" min="0" step="0.1" value={deliverySettings.delivery_fee_per_km}
                        onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_fee_per_km: e.target.value }))}
                        style={{ width: 90, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                    </div>
                  </>
                )}

                {(deliverySettings.delivery_mode === 'platform_pool' || deliverySettings.delivery_fee_model === 'base_plus_per_km') && (
                  <>
                    <div>
                      <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Baza haqqı (₼)</span>
                      <input type="number" min="0" step="0.5" value={deliverySettings.delivery_fee_base}
                        onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_fee_base: e.target.value }))}
                        style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                    </div>
                    <div>
                      <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>₼/km</span>
                      <input type="number" min="0" step="0.1" value={deliverySettings.delivery_fee_per_km}
                        onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_fee_per_km: e.target.value }))}
                        style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                    </div>
                  </>
                )}

                <div>
                  <span style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Min. sifariş (₼)</span>
                  <input type="number" min="0" step="1" value={deliverySettings.delivery_min_order}
                    onChange={e => setDeliverySettings(prev => ({ ...prev, delivery_min_order: e.target.value }))}
                    style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '2px solid #ddd', textAlign: 'center' }} />
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button type="button" className="save-button" disabled={savingDelivery || !activeLocation} onClick={handleDeliverySave}>
              {savingDelivery ? 'Yadda saxlanılır...' : '💾 Çatdırılma Ayarlarını Saxla'}
            </button>
          </div>
        </div>
      </div>


      {/* ── Şifrə Dəyişikliyi ── */}
      <div className="profile-card">
        <div className="password-section">
          <h2>🔒 Şifrə Dəyişikliyi</h2>
          {!changingPassword ? (
            <button className="change-password-button" onClick={() => setChangingPassword(true)}>
              🔑 Şifrə Dəyiş
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Yeni Şifrə</label>
                <input type="password" value={passwordData.newPassword}
                  onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="••••••••" minLength={6} required />
              </div>
              <div className="form-group">
                <label>Yeni Şifrə Təkrar</label>
                <input type="password" value={passwordData.confirmPassword}
                  onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="••••••••" required />
              </div>
              <div className="form-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Dəyişdirilir...' : '💾 Şifrəni Dəyiş'}
                </button>
                <button type="button" className="cancel-btn"
                  onClick={() => setChangingPassword(false)} disabled={loading}>❌ Ləğv Et</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Çıxış ── */}
      <div className="profile-card danger-zone">
        <h2>🚪 Hesabdan Çıxış</h2>
        <p>Hesabınızdan çıxmaq istəyirsiniz?</p>
        <button className="logout-danger-button" onClick={logout}>🚪 Çıxış</button>
      </div>
    </div>
  );
};

export default ProfilePage;

// Chain owner inline edit styles
const cs = {
  field:     { display: 'flex', flexDirection: 'column', gap: 4 },
  label:     { fontSize: 12, fontWeight: 600, color: '#555' },
  input:     { padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fafafa' },
  editBtn:   { padding: '7px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  saveBtn:   { padding: '9px 22px', borderRadius: 9, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  cancelBtn: { padding: '9px 18px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', color: '#666', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
};
